import { MAX_GUESSES, WORD_LENGTH } from "@/app/(public)/(products)/games/wordle/_logic/constants";

/**
 * Constants for the RISE countdown games: eight NOLA-themed daily puzzles played by the
 * eaMAFIA community in the run-up to RISE in New Orleans, with a shared points leaderboard.
 */

/** Campaign key stored on every player and play row, so a future countdown can reuse the tables. */
export const RISE_CAMPAIGN = "rise-2026";

/**
 * Campaign key on every non-production deploy. Preview deploys share the production database, so
 * test plays (and the fake clock) must never land in {@link RISE_CAMPAIGN}, where a test row would
 * take a top-5 bonus slot or rename a real player on the live leaderboard.
 */
export const RISE_PREVIEW_CAMPAIGN = "rise-2026-preview";

/** Stacking order for the solve confetti (matches js-confetti's own default). */
export const RISE_CONFETTI_Z_INDEX = 1000;

/** Every day's unlock and close time is wall-clock time in this zone. */
export const RISE_TIMEZONE = "America/Los_Angeles";

/** A day's game unlocks at this hour (24h, in {@link RISE_TIMEZONE}) on its date. */
export const RISE_UNLOCK_HOUR = 9;

/**
 * The last game closes at this local time on the last day, so the final leaderboard is settled
 * before the winners post goes out that afternoon. Every other day closes when the next one unlocks.
 */
export const RISE_FINAL_CLOSE_HOUR = 17;

/** Points for solving the day's game. */
export const RISE_POINTS_SOLVE = 3;

/** Bonus points for being one of the first {@link RISE_FAST_SOLVER_COUNT} solvers of the day. */
export const RISE_POINTS_FAST_BONUS = 2;

/** How many of the day's earliest solvers get the fast-solver bonus. */
export const RISE_FAST_SOLVER_COUNT = 5;

/** Bonus points per consecutive solved day in the player's streak, today included. */
export const RISE_POINTS_PER_STREAK_DAY = 1;

/** Wordle: guesses allowed. Shared with the main Wordle so the two boards can't drift. */
export const RISE_WORDLE_MAX_GUESSES = MAX_GUESSES;

/** Wordle: letters per word. Shared with the main Wordle so the two boards can't drift. */
export const RISE_WORDLE_LENGTH = WORD_LENGTH;

/** Connections: wrong guesses allowed before the game ends. */
export const RISE_CONNECTIONS_MAX_MISTAKES = 4;

/** Connections: tiles per group. */
export const RISE_CONNECTIONS_GROUP_SIZE = 4;

/** Rows shown on the public leaderboard. */
export const RISE_LEADERBOARD_LIMIT = 25;

/** Display name length limits on the join form. */
export const RISE_NAME_MIN_LENGTH = 2;
export const RISE_NAME_MAX_LENGTH = 40;

/** HttpOnly cookie holding the player's id once they join. */
export const RISE_PLAYER_COOKIE = "rise_games_player";

/** The player cookie outlives the campaign by a comfortable margin. */
export const RISE_PLAYER_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60;

/**
 * Lets a non-production deploy pretend it is another time (ISO string), so the team can play
 * through every day before launch. Ignored on the production deploy.
 */
export const RISE_FAKE_NOW_ENV = "RISE_GAMES_FAKE_NOW";

/** Public hub route. */
export const RISE_HUB_PATH = "/games/rise";

/**
 * Route for one day's game.
 *
 * @param day - Campaign day number, 1-based.
 * @returns The day's play route.
 */
export const buildRiseDayPath = (day: number): string => `${RISE_HUB_PATH}/${day}`;
